import { HelpCircle, Copy, Check } from "lucide-react";
import { formatShortWalletAddress } from "../lib/formatShortWalletAddress";
import { getETA } from "../lib/getETAWarning";

type Props = {
  gasRefuelUSD?: string;
  totalFeeUSD?: string;
  recipient?: string;
  isEmailRecipient?: boolean;
  etaInSeconds?: number;
  showETA?: boolean;
  processingFeeDisclaimer?: string;
  onNavigateToGasRefillExplainer?: () => void;
  onCopyRecipient?: (address: string) => void;
  isCopied?: boolean;
};

export function FeeBreakdown({
  gasRefuelUSD,
  totalFeeUSD,
  recipient,
  isEmailRecipient = false,
  etaInSeconds,
  showETA = true,
  processingFeeDisclaimer,
  onNavigateToGasRefillExplainer,
  onCopyRecipient,
  isCopied = false,
}: Props) {
  return (
    <div className="space-y-1">
      {gasRefuelUSD && Number(gasRefuelUSD) > 0 ? (
        <div className="flex justify-between items-center relative">
          <div className="text-xs text-[--color-text-secondary] flex items-center gap-1">
            Gas Refill
            <button
              type="button"
              className="focus:outline-none flex items-center justify-center"
              onClick={() =>
                onNavigateToGasRefillExplainer &&
                onNavigateToGasRefillExplainer()
              }
              aria-label="Gas Refill explanation"
            >
              <HelpCircle
                className="w-3.5 h-3.5 text-[--color-text-secondary] hover:text-[--color-text-primary] transition-colors"
                strokeWidth={2}
              />
            </button>
          </div>
          <div className="text-sm text-[--color-text-primary]">
            $
            {Number(gasRefuelUSD).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </div>
        </div>
      ) : null}

      {totalFeeUSD ? (
        <div className="flex justify-between items-center">
          <div className="text-xs text-[--color-text-secondary]">
            Processing Fee{processingFeeDisclaimer ? "*" : ""}
          </div>
          <div className="text-sm text-[--color-text-primary]">
            $
            {Number(totalFeeUSD).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </div>
        </div>
      ) : null}

      {recipient ? (
        <div className="flex justify-between items-center w-full">
          <div className="text-xs text-[--color-text-secondary]">Recipient</div>
          {!isEmailRecipient ? (
            <div className="flex items-center gap-1">
              <button
                type="button"
                className="p-1 hover:bg-[--color-background-secondary] rounded transition-colors"
                onClick={() => onCopyRecipient && onCopyRecipient(recipient)}
                aria-label="Copy recipient address"
              >
                {isCopied ? (
                  <Check className="w-4 h-4 text-green-500" strokeWidth={2} />
                ) : (
                  <Copy
                    className="w-4 h-4 text-[--color-text-secondary]"
                    strokeWidth={2}
                  />
                )}
              </button>
              <div className="text-sm text-[--color-text-primary]">
                {formatShortWalletAddress(recipient)}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-1 w-2/3 justify-end">
              <button
                type="button"
                className="p-1 hover:bg-[--color-background-secondary] rounded transition-colors flex-shrink-0"
                onClick={() => onCopyRecipient && onCopyRecipient(recipient)}
                aria-label="Copy recipient address"
              >
                {isCopied ? (
                  <Check className="w-4 h-4 text-green-500" strokeWidth={2} />
                ) : (
                  <Copy
                    className="w-4 h-4 text-[--color-text-secondary]"
                    strokeWidth={2}
                  />
                )}
              </button>
              <div className="text-sm text-[--color-text-primary] text-ellipsis overflow-hidden">
                {recipient}
              </div>
            </div>
          )}
        </div>
      ) : null}

      {showETA && etaInSeconds ? (
        <div className="flex justify-between items-center">
          <div className="text-xs text-[--color-text-secondary]">ETA</div>
          <div
            className={`text-sm transition-colors ${
              etaInSeconds > 10
                ? "text-[--color-alert-warning-accent]"
                : "text-[--color-text-primary]"
            }`}
          >
            {getETA(etaInSeconds)}
          </div>
        </div>
      ) : null}
    </div>
  );
}
